import { useState, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { findJobs } from '../../services/api'
import './FindJobsModal.scss'

const { logger } = Sentry;

interface JobResult {
  title: string
  company: string
  location: string
  url: string
  snippet: string
  source: string
  salary?: string | null
  type?: string | null
  updated?: string | null
}

interface FindJobsModalProps {
  isOpen: boolean
  onClose: () => void
  onImport: (jobData: any) => Promise<void>
  isImporting: boolean
  userCredits?: number
  // Persisted state from parent
  jobs?: JobResult[]
  keywords?: string
  onJobsChange?: (jobs: JobResult[]) => void
  onKeywordsChange?: (keywords: string) => void
  // Track imported jobs
  importedJobUrls?: Set<string>
  onJobImported?: (url: string) => void
}

function FindJobsModal({ 
  isOpen, 
  onClose, 
  onImport, 
  isImporting, 
  userCredits,
  jobs: externalJobs,
  keywords: externalKeywords,
  onJobsChange,
  onKeywordsChange,
  importedJobUrls,
  onJobImported
}: FindJobsModalProps) {
  const { getAccessTokenSilently } = useAuth0()
  const [isSearching, setIsSearching] = useState(false)
  // Use external state if provided, otherwise use internal state
  const [internalJobs, setInternalJobs] = useState<JobResult[]>([])
  const [internalKeywords, setInternalKeywords] = useState<string>('')
  const jobs = externalJobs !== undefined ? externalJobs : internalJobs
  const keywords = externalKeywords !== undefined ? externalKeywords : internalKeywords
  const [error, setError] = useState<string | null>(null)
  const [importingJobs, setImportingJobs] = useState<Set<string>>(new Set())

  // Helper functions to update jobs and keywords
  const setJobs = (newJobs: JobResult[]) => {
    if (onJobsChange) {
      onJobsChange(newJobs)
    } else {
      setInternalJobs(newJobs)
    }
  }

  const setKeywords = (newKeywords: string) => {
    if (onKeywordsChange) {
      onKeywordsChange(newKeywords)
    } else {
      setInternalKeywords(newKeywords)
    }
  }

  // Reset state when modal closes (only if using internal state)
  useEffect(() => {
    if (!isOpen && externalJobs === undefined) {
      setInternalJobs([])
      setInternalKeywords('')
      setError(null)
      setImportingJobs(new Set())
    } else if (!isOpen) {
      // If using external state, just clear error and importing jobs
      setError(null)
      setImportingJobs(new Set())
    }
  }, [isOpen, externalJobs])

  const handleFindJobs = async () => {
    setIsSearching(true)
    setError(null)
    setJobs([])

    try {
      const result = await Sentry.startSpan(
        {
          op: 'http.client',
          name: 'Find Jobs Based on Profile',
        },
        async () => {
          return await findJobs(getAccessTokenSilently)
        }
      )

      setKeywords(result.keywords || '')
      setJobs(result.jobs || [])
      
      if (!result.jobs || result.jobs.length === 0) {
        setError('No jobs found. Try updating your profile with more experience, education, or skills.')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err)
      logger.error('Error finding jobs', {
        error: errorMessage,
      });
      setError(errorMessage.includes('Profile') 
        ? errorMessage 
        : 'Failed to find jobs. Please try again later.')
    } finally {
      setIsSearching(false)
    }
  }

  const handleImportJob = async (job: JobResult) => {
    if (importingJobs.has(job.url)) return
    
    setImportingJobs(prev => new Set(prev).add(job.url))
    
    try {
      // Reuse the data from search results instead of parsing the URL again
      // This saves credits and time, and the search results already have good data
      let companyWebsite = ''
      
      // Extract company website from URL
      if (job.url) {
        try {
          const urlObj = new URL(job.url)
          companyWebsite = urlObj.hostname.replace('www.', '')
        } catch {
          // Ignore URL parsing errors
        }
      }

      const jobData = {
        title: job.title,
        company: job.company,
        companyWebsite: companyWebsite,
        location: job.location,
        description: job.snippet || '',
        url: job.url
      }

      logger.info('Importing job from search results', {
        url: job.url,
        title: job.title,
        company: job.company,
      });

      await onImport(jobData)
      // Mark this job as imported
      if (onJobImported) {
        onJobImported(job.url)
      }
      // Modal will be closed by the parent component (Dashboard) after successful import
    } catch (importError) {
      logger.error('Error importing job', {
        error: importError instanceof Error ? importError.message : String(importError),
        url: job.url,
      });
      throw importError // Re-throw to allow caller to handle
    } finally {
      setImportingJobs(prev => {
        const newSet = new Set(prev)
        newSet.delete(job.url)
        return newSet
      })
    }
  }

  const handleClose = () => {
    if (!isSearching && importingJobs.size === 0) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay">
      <div className="modal-content find-jobs-modal">
        <div className="modal-header">
          <h3 className="modal-title">Find Jobs for Me</h3>
          <button 
            type="button" 
            className="modal-close" 
            onClick={handleClose}
            disabled={isSearching || isImporting || importingJobs.size > 0}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {userCredits !== undefined && userCredits <= 0 && (
            <div className="alert alert-warning mb-3" role="alert">
              <strong>No credits remaining!</strong> You need at least 1 credit to import each job posting. 
              Please purchase more credits to continue.
            </div>
          )}

          {jobs.length === 0 && !isSearching && !error && (
            <div className="find-jobs-intro">
              <p className="mb-3">
                We'll analyze your profile (experience, education, and skills) to generate relevant job search keywords, 
                then search for matching job postings.
              </p>
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={handleFindJobs}
                disabled={isSearching}
                style={{ width: 'auto', minWidth: '200px' }}
              >
                {isSearching ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Finding jobs...
                  </>
                ) : (
                  <>
                    <i className="bi bi-search me-2"></i>
                    Find Jobs for Me
                  </>
                )}
              </button>
            </div>
          )}

          {isSearching && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Searching...</span>
              </div>
              <p>Analyzing your profile and searching for jobs...</p>
            </div>
          )}

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {keywords && keywords.trim().length > 0 && (
            <div className="mb-3">
              <small className="text-muted">
                <strong>Search keywords used:</strong> {keywords}
              </small>
            </div>
          )}

          {jobs.length > 0 && (
            <>
              <div className="mb-3">
                <strong>{jobs.length}</strong> job{jobs.length !== 1 ? 's' : ''} found
              </div>

              <div className="job-results-list">
                {jobs.map((job, index) => {
                  const isImportingJob = importingJobs.has(job.url)
                  const isImported = importedJobUrls?.has(job.url) || false
                  // If credits are undefined, assume they have credits (data not loaded yet)
                  // If credits are 0, disable all buttons
                  // Otherwise, only disable the specific job being imported or already imported
                  const hasCredits = userCredits === undefined || userCredits > 0
                  const canImport = hasCredits && !isImportingJob && !isImported
                  
                  return (
                    <div 
                      key={index} 
                      className={`job-result-card ${isImportingJob ? 'importing' : ''}`}
                    >
                      <div className="job-result-content">
                        <div className="job-result-header">
                          <h5 className="job-title">{job.title}</h5>
                          <div className="job-meta">
                            <span className="job-company">{job.company}</span>
                            {job.location && (
                              <span className="job-location">
                                <i className="bi bi-geo-alt me-1"></i>
                                {job.location}
                              </span>
                            )}
                          </div>
                        </div>
                        {job.snippet && (
                          <p className="job-snippet">{job.snippet}</p>
                        )}
                        <div className="job-actions">
                          <a 
                            href={job.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="job-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View job posting <i className="bi bi-box-arrow-up-right ms-1"></i>
                          </a>
                          <button
                            type="button"
                            className={`btn btn-sm ${isImported ? 'btn-success' : 'btn-primary'}`}
                            onClick={() => handleImportJob(job)}
                            disabled={!canImport}
                            title={!hasCredits ? 'No credits remaining. Please purchase more credits.' : isImported ? 'This job has already been imported' : ''}
                          >
                            {isImportingJob ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                Importing...
                              </>
                            ) : isImported ? (
                              <>
                                <i className="bi bi-check-circle me-2"></i>
                                Imported
                              </>
                            ) : !hasCredits ? (
                              <>
                                <i className="bi bi-download me-2"></i>
                                Import Job Posting (No credits)
                              </>
                            ) : (
                              <>
                                <i className="bi bi-download me-2"></i>
                                Import Job Posting (1 credit)
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {jobs.length > 0 && (
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleClose}
              disabled={importingJobs.size > 0}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default FindJobsModal

